import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { PiSquare, PiCheckSquareDuotone, PiMinusSquareDuotone } from 'react-icons/pi';
import { MdInfo } from 'react-icons/md';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import DropdownSelect from 'components/DropdownSelect';
import Header from 'components/Header';
import TextInput from 'components/TextInput';
import Table from 'components/Table';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

import './index.css';
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const NUM_QUESTIONS = {
    'Reading and Writing': 27,
    'Math': 22,
};
const DIFFICULTY_THRESHOLDS = {
    HARD: 40,
    MEDIUM: 80,
    EASY: 100,
};

function CreateAssignment() {
    const [questions, setQuestions] = useState(null);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [categoryCounts, setCategoryCounts] = useState(null);
    const [testTypes, setTestTypes] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [difficultyLevels, setDifficultyLevels] = useState(Object.keys(DIFFICULTY_THRESHOLDS));
    const [assignmentName, setAssignmentName] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [selectedTestType, setSelectedTestType] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedDomains, setSelectedDomains] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [onlyNotInActiveTests, setOnlyNotInActiveTests] = useState(false);
    const [maxNumQuestions, setMaxNumQuestions] = useState(27);
    const [assignmentInfo, setAssignmentInfo] = useState(false);
    const [showSelected, setShowSelected] = useState(false);
    const [questionId, setQuestionId] = useState(null);
    const [creating, setCreating] = useState(false);

    const { user } = useAuthContext();
    const navigate = useNavigate();
    const tableRef = useRef<HTMLTableElement>();

    const selectQuestion = (question) => {
        const index = selectedQuestions.findIndex(q => q.id === question.id);

        if (index > -1) {
            let newSelectedQuestions = [...selectedQuestions];
            newSelectedQuestions.splice(index, 1);
            setSelectedQuestions(newSelectedQuestions);
        }
        else if (selectedQuestions.length < NUM_QUESTIONS[selectedSubject]) {
            setSelectedQuestions([
                ...selectedQuestions,
                question
            ]);
        }
    }

    const selectDomain = (domain) => {
        const index = selectedDomains.findIndex(d => d.domain === domain.domain);

        let newSelectedDomains = [...selectedDomains];
        let newSelectedCategories = [...selectedCategories];

        if (newSelectedDomains[index].selected) {
            newSelectedDomains[index].selected = false;
            for (let i = 0; i < newSelectedCategories.length; i++) {
                if (newSelectedCategories[i].domain === domain.domain) {
                    newSelectedCategories[i].selected = false;
                }
            }
        }
        else {
            newSelectedDomains[index].selected = true;
            for (let i = 0; i < newSelectedCategories.length; i++) {
                if (newSelectedCategories[i].domain === domain.domain) {
                    newSelectedCategories[i].selected = true;
                }
            }
        }

        setSelectedDomains(newSelectedDomains);
        setSelectedCategories(newSelectedCategories);
    }

    const selectCategory = (category) => {
        const index = selectedCategories.findIndex(c => c.category === category.category);

        let newSelectedCategories = [...selectedCategories];

        if (newSelectedCategories[index].selected) {
            newSelectedCategories[index].selected = false;
        }
        else {
            newSelectedCategories[index].selected = true;
        }

        setSelectedCategories(newSelectedCategories);
    }

    const selectDifficulty = (difficulty) => {
        const index = difficultyLevels.findIndex(d => d === difficulty);
        let newDifficultyLevels = [...difficultyLevels];

        if (index < 0) {
            newDifficultyLevels.push(difficulty);
        }
        else {
            newDifficultyLevels.splice(index, 1);
        }
        setDifficultyLevels(newDifficultyLevels);
    }

    const selectAll = () => {
        const tableQuestions = Array.from(tableRef.current.getElementsByClassName('createAssignment__questionId')).map(el => el.id);
        
        if (tableQuestions.every(questionId => selectedQuestions.find(question => question.id === questionId)) || 
            selectedQuestions.length >= maxNumQuestions
        ) {
            let newSelectedQuestions = [...selectedQuestions];

            for (let i = 0; i < tableQuestions.length; i++) {
                let index = newSelectedQuestions.findIndex(question => question.id === tableQuestions[i]);
                if (index >= 0) {
                    newSelectedQuestions.splice(index, 1);
                }
            }

            setSelectedQuestions(newSelectedQuestions);
        }
        else {
            let newSelectedQuestions = [...selectedQuestions];
            
            for (let i = 0; i < tableQuestions.length; i++) {
                if (newSelectedQuestions.length < maxNumQuestions) {
                    if (newSelectedQuestions.find(question => question.id === tableQuestions[i])) continue;

                    newSelectedQuestions.push(questionsList.find(question => question.id === tableQuestions[i]));
                }
                else {
                    break;
                }
            }
            setSelectedQuestions(newSelectedQuestions);
        }
    }

    const createAssignment = () => {
        setCreating(true);
        axios.post('tutors/assignments/create/', {
                organization: selectedOrganization?.name,
                name: assignmentName,
                testType: selectedTestType,
                subject: selectedSubject,
                questions: selectedQuestions
            })
            .then(res => {
                setCreating(false);
                navigate('/assignments');
            })
            .catch(error => {
                setCreating(false);
                console.log(error);
            });
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (selectedSubject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        if (user?.organizations?.length > 0) {
            setSelectedOrganization(user.organizations[0]);
        }
    }, [user]);

    useEffect(() => {
        axios.get('tutors/questions/')
            .then(res => {
                // console.log(res.data);
                setQuestions(res.data.questions);
                setTestTypes(Object.keys(res.data.categoryCounts));
                setCategoryCounts(res.data.categoryCounts);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (selectedSubject) {
            let allDomains = [];
            let allCategories = [];
            for (const domain in categoryCounts[selectedTestType][selectedSubject]) {
                allDomains.push({
                    domain: domain,
                    selected: true
                });
                for (const category in categoryCounts[selectedTestType][selectedSubject][domain]) {
                    allCategories.push({
                        domain: domain,
                        category: category,
                        selected: true
                    });
                }
            }
            setSelectedDomains(allDomains);
            setSelectedCategories(allCategories);

            if (selectedSubject in NUM_QUESTIONS) {
                setMaxNumQuestions(NUM_QUESTIONS[selectedSubject]);
            }
        }
        else if (selectedTestType) {
            setSubjects(Object.keys(categoryCounts[selectedTestType]));
        }
    }, [selectedTestType, selectedSubject]);

    useEffect(() => {
        setQuestionId(null);
    }, [selectedDomains, selectedCategories, showSelected]);

    useEffect(() => {
        if (questionId) {
            renderKatex();
        }
    }, [questionId]);

    if (questions === null) return <Loader />;

    else if (!assignmentInfo) {
        return (
            <div className='createAssignment'>
                <Helmet>
                    <title>
                        Create Assignment - Test Center Admin
                    </title>
                </Helmet>
                <Header title='Create Assignment' />
                <div className='createAssignment__bar'>
                    <h3 className='createAssignment__header'>Create a new assignment</h3>
                    <Button
                        className='createAssignment__button'
                        onClick={() => setAssignmentInfo(true)}
                        disabled={!assignmentName || !selectedOrganization || !selectedTestType || !selectedSubject}
                    >
                        Next
                    </Button>
                    <Button className='createAssignment__button' onClick={() => navigate('/assignments')} secondary>
                        Cancel
                    </Button>
                </div>
                <Dropdown
                    className='createAssignment__choice'
                    label='Organization'
                    data={user.organizations}
                    dataKey='name'
                    placeholder='select organization'
                    value={selectedOrganization?.name}
                    onSelect={value => setSelectedOrganization(value)}
                    required
                />
                <TextInput
                    className='createAssignment__choice'
                    label='Assignment Name'
                    value={assignmentName}
                    onChange={event => setAssignmentName(event.target.value)}
                    required
                />
                <Dropdown
                    className='createAssignment__choice'
                    data={testTypes}
                    onSelect={(testType) => setSelectedTestType(testType)}
                    label='Test Type'
                    required
                    value={selectedTestType}
                />
                {subjects.length > 0 && <>
                    <Dropdown
                        data={subjects}
                        onSelect={(subject) => setSelectedSubject(subject)}
                        label='Section'
                        required
                        value={selectedSubject}
                    />
                    <div className='createAssignment__info'>
                        <MdInfo className='createAssignment__infoIcon' />
                        <p>
                            27 questions for Reading and Writing<br/>
                            22 questions for Math
                        </p>
                    </div>
                </>}
            </div>
        );
    }

    const filteredQuestions = questions.filter(question => (
            question.testType === selectedTestType &&
            question.subject === selectedSubject &&
            selectedDomains.find(domain => domain.selected && domain.domain === question.rootCategory) &&
            selectedCategories.find(category => category.selected && category.category === question.category) &&
            (!onlyNotInActiveTests || question.isActive === false) &&
            (question.percentageCorrect === null ||
                (question.percentageCorrect <= DIFFICULTY_THRESHOLDS.HARD && difficultyLevels.includes('HARD')) ||
                (question.percentageCorrect <= DIFFICULTY_THRESHOLDS.MEDIUM && 
                    question.percentageCorrect > DIFFICULTY_THRESHOLDS.HARD &&
                    difficultyLevels.includes('MEDIUM')
                ) ||
                (question.percentageCorrect > DIFFICULTY_THRESHOLDS.MEDIUM && difficultyLevels.includes('EASY'))
            )
        ));

    const questionsList = showSelected
        ? questions.filter(question => selectedQuestions.find(q => q.id === question.id))
        : filteredQuestions;

    const showQuestion = questions.find(question => question.id === questionId);

    return (
        <div className='createAssignment'>
            <Helmet>
                <title>
                    Create Assignment - Test Center Admin
                </title>
            </Helmet>
            <Header title='Create Assignment' />
            <div className='createAssignment__bar'>
                <h3 className='createAssignment__header'>{selectedTestType} {selectedSubject} Questions</h3>
                <Button className='createAssignment__button' onClick={createAssignment} disabled={selectedQuestions.length < maxNumQuestions || creating}>
                    Create
                </Button>
                <Button className='createAssignment__button' onClick={() => navigate('/assignments')} secondary>
                    Cancel
                </Button>
            </div>
            <div className='createAssignment__filter'>
                <DropdownSelect
                    className='createAssignment__filterDropdown'
                    data={selectedDomains}
                    dataKey='domain'
                    label='Domains'
                    selected={selectedDomains.map(domain => domain.selected)}
                    onSelect={selectDomain}
                />
                <DropdownSelect
                    className='createAssignment__filterDropdown'
                    data={selectedCategories.filter(category => selectedDomains.find(domain => (
                        domain.selected && domain.domain === category.domain
                    )))}
                    dataKey='category'
                    groupByKey='domain'
                    selectedKey='selected'
                    label='Categories'
                    selected={selectedCategories.filter(category => selectedDomains.find(domain => (
                        domain.selected && domain.domain === category.domain
                    ))).map(category => category.selected)}
                    onSelect={selectCategory}
                />
                <DropdownSelect
                    className='createAssignment__filterDropdown'
                    data={Object.keys(DIFFICULTY_THRESHOLDS)}
                    label='Difficulty'
                    selected={Object.keys(DIFFICULTY_THRESHOLDS).map(difficulty => difficultyLevels.includes(difficulty))}
                    onSelect={selectDifficulty}
                />
            </div>
            <label className='createAssignment__notInActive'>
                <input
                    type='checkbox'
                    checked={onlyNotInActiveTests}
                    onChange={() => setOnlyNotInActiveTests(prev => !prev)}
                />
                Not in active tests
            </label>
            <div className='createAssignment__tableHeader'>
                <p className='createAssignment__questionCount'>
                    <b>{selectedQuestions.length}</b> out of <b>{maxNumQuestions}</b> questions selected.
                    {/* <b>{filteredQuestions.length} questions in results set.</b> */}
                </p>
                <div className='createAssignment__select'>
                    <p className={showSelected
                        ? 'createAssignment__selected--active'
                        : 'createAssignment__selected'
                    } onClick={() => setShowSelected(true)}>
                        Show selected questions ({selectedQuestions.length})
                    </p>
                    <p>&nbsp;|&nbsp;</p>
                    <p className={showSelected
                        ? 'createAssignment__selected'
                        : 'createAssignment__selected--active'
                    } onClick={() => setShowSelected(false)}>
                        Show All ({filteredQuestions.length})
                    </p>
                </div>
            </div>
            <div className='createAssignment__body'>
                <Table className='createAssignment__questions'
                    key={questionsList.length}
                    header={questionId
                        ? [
                            selectedQuestions.length > 0
                                ? <PiCheckSquareDuotone className='createAssignment__checkBox--active' onClick={selectAll} />
                                : <PiSquare className='createAssignment__checkBox' onClick={selectAll} />,
                            'Question'
                        ] : [
                            selectedQuestions.length > 0
                                ? <PiCheckSquareDuotone className='createAssignment__checkBox--active' onClick={selectAll} />
                                : <PiSquare className='createAssignment__checkBox' onClick={selectAll} />,
                            'Question',
                            'Domain',
                            'Category',
                            'Difficulty'
                        ]
                    }
                    columnTypes={questionId
                        ? [
                            null,
                            'string'
                        ] : [
                            null,
                            'string',
                            'string',
                            'string',
                            'number'
                        ]
                    }
                    sortValues={questionsList.map(question => (questionId
                        ? [
                            null,
                            question.id.split(' ').pop(),
                        ] : [
                            null,
                            question.id.split(' ').pop(),
                            question.rootCategory,
                            question.category,
                            question.percentageCorrect
                        ]
                    ))}
                    rows={questionsList.map(question => (questionId
                        ? [
                            selectedQuestions.find(q => q.id === question.id)
                                ? <PiCheckSquareDuotone className='createAssignment__checkBox--active' />
                                : <PiSquare className='createAssignment__checkBox' />,
                            <span id={question.id} className='createAssignment__questionId'>{question.id.split(' ').pop()}</span>,
                        ] : [
                            selectedQuestions.find(q => q.id === question.id)
                                ? <PiCheckSquareDuotone className='createAssignment__checkBox--active' />
                                : <PiSquare className='createAssignment__checkBox' />,
                            <span id={question.id} className='createAssignment__questionId'>{question.id.split(' ').pop()}</span>,
                            question.rootCategory,
                            question.category,
                            question.percentageCorrect !== null
                                ? (question.percentageCorrect > DIFFICULTY_THRESHOLDS.MEDIUM
                                    ? <span className='createAssignment__easy'>Easy</span>
                                    : question.percentageCorrect > DIFFICULTY_THRESHOLDS.HARD
                                    ? <span className='createAssignment__medium'>Medium</span>
                                    : <span className='createAssignment__hard'>Hard</span>
                                  ) 
                                : null
                        ]
                    ))}
                    // onSelectRow={questionsList.map(question => (
                    //     () => question.id === questionId
                    //         ? setQuestionId(null)
                    //         : setQuestionId(question.id)
                    // ))}
                    rowsSelected={questionsList.map(question => (
                        question.id === questionId
                    ))}
                    onSelectCell={questionsList.map(question => (questionId
                        ? [
                            () => selectQuestion(question),
                            () => question.id === questionId
                                ? setQuestionId(null)
                                : setQuestionId(question.id),
                        ] : [
                            () => selectQuestion(question),
                            () => question.id === questionId
                                ? setQuestionId(null)
                                : setQuestionId(question.id),
                            null,
                            null,
                            null
                        ]
                    ))}
                    tableRef={tableRef}
                />
                {questionId && showQuestion &&
                    <div className='createAssignment__question'>
                        <p className='createAssignment__questionInfo'>Domain: {showQuestion.rootCategory}</p>
                        <p className='createAssignment__questionInfo'>Category: {showQuestion.category}</p>
                        <p className='createAssignment__questionInfo'>Difficulty: {(showQuestion.percentageCorrect === null
                            ? <span>-</span>
                            : showQuestion.percentageCorrect <= DIFFICULTY_THRESHOLDS.HARD
                            ? <span className='createAssignment__hard'>Hard</span>
                            : showQuestion.percentageCorrect <= DIFFICULTY_THRESHOLDS.MEDIUM
                            ? <span className='createAssignment__medium'>Medium</span>
                            : <span className='createAssignment__easy'>Easy</span>
                        )}</p>
                        <div className='createAssignment__questionContent'>
                            {showQuestion.figure &&
                                <img
                                    className='createAssignment__figure'
                                    key={showQuestion.figure}
                                    src={showQuestion.figure}
                                    alt={showQuestion.id}
                                />
                            }
                            {showQuestion.passage &&
                                <p className='katex createAssignment__passage'>
                                    {showQuestion.passage}
                                </p>
                            }
                            <p className='katex createAssignment__questionQuestion'>
                                {showQuestion.question}
                            </p>
                            {showQuestion.choices.length > 1
                            ?   showQuestion.choices.map((choice, c) => (
                                    <div className='createAssignment__questionChoiceWrapper'>
                                        <div className='createAssignment__questionChoice'>
                                            <div className='createAssignment__questionChoiceOutline'></div>
                                            <p className='createAssignment__questionChoiceLetter'>
                                                {MULTIPLE_CHOICES[c]}
                                            </p>
                                            <p className='createAssignment__questionChoiceText'>
                                                <span className='katex'>
                                                    {choice}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                ))
                            :   showQuestion.choiceFigures.length > 1
                            ?   showQuestion.choiceFigures.map((choiceFigure, c) => (
                                <div className='createAssignment__questionChoiceWrapper'>
                                    <div className='createAssignment__questionChoice'>
                                        <div className='createAssignment__questionChoiceOutline'></div>
                                        <p className='createAssignment__questionChoiceLetter'>
                                            {MULTIPLE_CHOICES[c]}
                                        </p>
                                        <img
                                            className='testPreview__questionChoiceFigure'
                                            key={choiceFigure}
                                            src={choiceFigure}
                                            alt={showQuestion.id}
                                            draggable={false}
                                        />
                                    </div>
                                </div>
                            ))
                            :   <></>
                        }
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default CreateAssignment;